/**
 * Copies a piece of text and says so for a moment.
 *
 * With a `label` it is a secondary button ("Copy", "Copy token"); without one
 * it is a small icon button for sitting beside a code sample or a link.
 */

import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { btnSecondary, FOCUS_RING } from '../mono/primitives';

export interface CopyButtonProps {
  text: string;
  label?: string;
  className?: string;
}

export function CopyButton({ text, label, className = '' }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  const icon = copied ? <Check className="h-3.5 w-3.5 text-[var(--green)]" /> : <Copy className="h-3.5 w-3.5" />;

  if (!label) {
    return (
      <button
        type="button"
        onClick={copy}
        title={copied ? 'Copied' : 'Copy to clipboard'}
        aria-label={copied ? 'Copied' : 'Copy to clipboard'}
        className={`p-1 text-[var(--text-ter)] hover:bg-[var(--hover-tint)] hover:text-[var(--text-pri)] transition-colors ${FOCUS_RING} ${className}`}
      >
        {icon}
      </button>
    );
  }

  const btn = btnSecondary(className);
  return (
    <button type="button" onClick={copy} className={btn.className} style={btn.style}>
      {icon}
      {copied ? 'Copied' : label}
    </button>
  );
}
